import type { DataTableDataSearchDto } from '@asset-metrix/portal-gateway-client-js';
import type { StateCreator } from 'zustand';

import { CUSTOM_FILTER_INSTANCE_ID_SUFFIX } from '../../utils/constants';
import type { RemoveFunctions } from '../../utils/types';
import type { StateSlices } from '..';

import { resetPaginationState } from './tablePagination';

export type FilterSelectionValue = NonNullable<
  DataTableDataSearchDto['selection']
>[string];

export type FilterSelections = {
  [filterKey: string]: FilterSelectionValue;
};

export type FilterSelectionSlice = {
  globalFilterEntityId: string | null;
  setGlobalFilterEntityId: (entityId: string | null) => void;
  filterSelections: {
    [entityId: string]: FilterSelections;
  };
  filterInitialized: {
    [entityId: string]: boolean;
  };
  filterQueryLoaded: {
    [entityId: string]: boolean;
  };
  setFilterSelectionValue: (
    entityId: string,
    key: string,
    selection: FilterSelectionValue | undefined
  ) => void;
  resetFilterSelections: (
    entityId: string,
    key?: string,
    omitKeys?: string[]
  ) => void;
  deleteFilterSelections: (entityId: string) => void;
  cloneFilterSelections: (entityIdFrom: string, entityIdTo: string) => void;
  setFilterInitialized: (entityId: string) => void;
  setFilterQueryLoaded: (entityId: string) => void;
  resetFilterQueryLoaded: (entityId: string) => void;
  presetFilterSelectionValue: (
    entityId: string,
    key: string,
    selection: FilterSelectionValue | undefined
  ) => void;
  batchPresetFilterSelections: (
    entityId: string,
    updates: FilterSelections,
    keysToRemove: string[]
  ) => void;
  resetFilterSelectionSlice: () => void;
};

const initialState: RemoveFunctions<FilterSelectionSlice> = {
  globalFilterEntityId: null,
  filterSelections: {},
  filterInitialized: {},
  filterQueryLoaded: {},
};

export const createFilterSelectionSlice: StateCreator<
  StateSlices,
  [],
  [],
  FilterSelectionSlice
> = (set) => ({
  ...initialState,

  setGlobalFilterEntityId: (entityId) => {
    set({ globalFilterEntityId: entityId });
  },

  setFilterSelectionValue: (entityId, key, selection) => {
    set((state) => {
      const entitySelections = { ...state.filterSelections[entityId] };
      if (selection === undefined) {
        delete entitySelections[key];
      } else {
        entitySelections[key] = selection;
      }
      const customSuffixId = `${entityId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`;
      return {
        filterSelections: {
          ...state.filterSelections,
          [entityId]: entitySelections,
        },
        pagination:
          entityId === state.globalFilterEntityId
            ? {}
            : resetPaginationState(state.pagination, entityId),
        filterQueryLoaded: {
          ...state.filterQueryLoaded,
          [entityId]: false,
          ...(customSuffixId in state.filterQueryLoaded
            ? { [customSuffixId]: false }
            : undefined),
        },
      };
    });
  },

  resetFilterSelections: (entityId, key, omitKeys) => {
    set((state) => {
      const pagination =
        entityId === state.globalFilterEntityId
          ? {}
          : resetPaginationState(state.pagination, entityId);
      const current = state.filterSelections[entityId];
      if (current == null) {
        if (pagination === state.pagination) {
          return state;
        }
        return { pagination };
      }

      const customSuffixId = `${entityId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`;
      const loadedReset = {
        ...state.filterQueryLoaded,
        [entityId]: false,
        ...(customSuffixId in state.filterQueryLoaded
          ? { [customSuffixId]: false }
          : undefined),
      };

      if (key != null) {
        const updated = { ...current };
        delete updated[key];
        return {
          filterSelections: {
            ...state.filterSelections,
            [entityId]: updated,
          },
          pagination,
          filterQueryLoaded: loadedReset,
        };
      }

      const updated: FilterSelections = {};
      for (const [k, v] of Object.entries(current)) {
        if (omitKeys?.includes(k)) {
          updated[k] = v;
        }
      }
      return {
        filterSelections: {
          ...state.filterSelections,
          [entityId]: updated,
        },
        pagination,
        filterQueryLoaded: loadedReset,
      };
    });
  },

  deleteFilterSelections: (entityId) => {
    set((state) => {
      const { [entityId]: _, ...rest } = state.filterSelections;
      const { [entityId]: __, ...restInit } = state.filterInitialized;
      const { [entityId]: ___, ...restLoaded } = state.filterQueryLoaded;
      return {
        filterSelections: rest,
        filterInitialized: restInit,
        filterQueryLoaded: restLoaded,
      };
    });
  },

  cloneFilterSelections: (entityIdFrom, entityIdTo) => {
    set((state) => ({
      filterSelections: {
        ...state.filterSelections,
        [entityIdTo]: { ...state.filterSelections[entityIdFrom] },
      },
      filterInitialized: {
        ...state.filterInitialized,
        [entityIdTo]: state.filterInitialized[entityIdFrom] ?? false,
      },
      filterQueryLoaded: {
        ...state.filterQueryLoaded,
        [entityIdTo]: state.filterQueryLoaded[entityIdFrom] ?? false,
      },
    }));
  },

  setFilterInitialized: (entityId) => {
    set((state) => ({
      filterInitialized: { ...state.filterInitialized, [entityId]: true },
    }));
  },

  setFilterQueryLoaded: (entityId) => {
    set((state) => {
      if (state.filterQueryLoaded[entityId]) {
        return state;
      }
      return {
        filterQueryLoaded: {
          ...state.filterQueryLoaded,
          [entityId]: true,
        },
      };
    });
  },

  resetFilterQueryLoaded: (entityId) => {
    set((state) => {
      if (!state.filterQueryLoaded[entityId]) {
        return state;
      }
      return {
        filterQueryLoaded: { ...state.filterQueryLoaded, [entityId]: false },
      };
    });
  },

  presetFilterSelectionValue: (entityId, key, selection) => {
    set((state) => {
      const entitySelections = { ...state.filterSelections[entityId] };
      if (selection === undefined) {
        delete entitySelections[key];
      } else {
        entitySelections[key] = selection;
      }
      return {
        filterSelections: {
          ...state.filterSelections,
          [entityId]: entitySelections,
        },
      };
    });
  },

  batchPresetFilterSelections: (entityId, updates, keysToRemove) => {
    set((state) => {
      const entitySelections = { ...state.filterSelections[entityId] };
      for (const key of keysToRemove) {
        delete entitySelections[key];
      }
      for (const [key, value] of Object.entries(updates)) {
        entitySelections[key] = value;
      }
      return {
        filterSelections: {
          ...state.filterSelections,
          [entityId]: entitySelections,
        },
      };
    });
  },

  resetFilterSelectionSlice: () => {
    set({ ...initialState });
  },
});
